import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpRequest } from '@/lib/axios';
export interface StockCategory {
    id: string;
    name: string;
    description: string | null;
    created_at: string;
    updated_at: string;
}

export interface StockItem {
    id: string;
    name: string;
    description: string | null;
    category_id: string | null;
    cost_price: number;
    sale_price: number;
    current_quantity: number;
    min_quantity: number;
    unit: string;
    category?: StockCategory;
    created_at: string;
    updated_at: string;
}

export type CreateStockItemDTO = {
    idempotency_key: string;
    name: string;
    description?: string;
    category_id?: string;
    cost_price: number;
    sale_price: number;
    current_quantity: number;
    min_quantity: number;
    unit: string;
};

export type UpdateStockItemDTO = Omit<CreateStockItemDTO, 'current_quantity'>;

export function useStockItems() {
    return useQuery({
        queryKey: ['stock-items'],
        queryFn: async () => {
            const response = await httpRequest.get('/stock-items');
            return response.data as StockItem[];
        },
    });
}

export function useCreateStockItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: CreateStockItemDTO) => {
            const response = await httpRequest.post('/stock-items', data);
            return response.data as StockItem;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['stock-items'] });
        },
    });
}

export function useUpdateStockItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<UpdateStockItemDTO> }) => {
            const response = await httpRequest.put(`/stock-items/${id}`, data);
            return response.data as StockItem;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['stock-items'] });
        },
    });
}

export function useDeleteStockItem() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await httpRequest.delete(`/stock-items/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['stock-items'] });
        },
    });
}
